import { runtimeConfig } from '@/config/runtime-config'
import { createApiClient } from '@/lib/api/http-client'
import { getStoredToken } from '@/lib/auth/token-storage'

interface SuccessEnvelope<T> {
  code: 'success'
  data: T
}

interface BatchSuccessEnvelope<T> {
  code: 'partial_success' | 'success'
  data: T
}

interface MessageEnvelope {
  code: 'success'
  message: string
}

interface OptionalDataEnvelope<T> {
  code: 'success'
  data?: T
  message?: string
}

export interface AdminOrganizationCompany {
  ad_guid?: string
  attributes?: Record<string, unknown>
  code?: string
  id: string
  name: string
  source?: string
  status?: number
  type?: string
}

export interface AdminOrganizationTreeNode {
  attributes?: Record<string, unknown>
  children?: AdminOrganizationTreeNode[]
  code?: string | null
  email?: string
  id: string
  is_default_department?: boolean
  is_expanded?: boolean
  member_count?: number
  name: string
  role?: string
  source?: string
  status?: 'active' | 'banned'
  type: 'company' | 'department' | 'member' | 'subdepartment'
}

export interface AdminMemberDepartment {
  attributes?: Record<string, unknown> | null
  code?: string | null
  id: string
  is_default_department?: boolean
  joined_at?: string | null
  name: string
  source?: string
  type?: string
}

export interface AdminMemberAccount {
  attributes?: Record<string, unknown> | null
  created_at?: string | null
  departments: AdminMemberDepartment[]
  email: string
  id: string
  last_active_at?: string | null
  name: string
  primary_department?: AdminMemberDepartment | null
  role: string
  status: 'active' | 'banned'
  updated_at?: string | null
}

export interface AdminMemberAccountsParams {
  order?: 'asc' | 'desc'
  orderBy?: string
  orgUnitId?: string
  page: number
  pageSize: number
  search?: string
  status?: 'active' | 'banned' | ''
}

export interface AdminMemberAccountsData {
  items: AdminMemberAccount[]
  page: number
  page_size: number
  total: number
}

export interface AdminMemberAccountAttributes {
  attributes: Record<string, unknown>
  email: string
  id: string
  name: string
}

export interface AdminBatchUserStatusResult {
  failed: { error: string; user_id: string }[]
  failed_count: number
  succeeded: { status: string; user_id: string }[]
  succeeded_count: number
  total: number
}

export interface AdminMemberAccountUpdatePayload {
  name: string
  userId: string
}

export interface AdminMemberCreateResult {
  email: string
  generated_password?: string
  id: string
  must_change_password: boolean
  name: string
  role: string
}

export interface AdminMemberPasswordResetResult {
  generated_password?: string
}

export interface AdminOrganizationSyncLockStatus {
  company_id: string | null
  company_ids: string[]
  is_syncing: boolean
  source: string | null
  started_at: string | null
}

export interface AdminOrganizationSyncRun {
  company_id: string | null
  company_ids: string[]
  completed_at: string | null
  duration_ms: number | null
  id: string
  message: string | null
  relations_count: number
  skipped_relations: number
  skipped_units: number
  source: string
  started_at: string | null
  status: 'abandoned' | 'failed' | 'running' | 'success'
  units_count: number
}

export interface AdminOrganizationSettings {
  organization_name: string
  sync: {
    active_lock: AdminOrganizationSyncLockStatus
    cron_expression: string
    is_enabled: boolean
    last_run: AdminOrganizationSyncRun | null
    sync_company_id: string | null
    sync_company_ids: string[]
    sync_scope: 'all' | 'selected'
    sync_source: string
  }
}

export interface AdminOrganizationSettingsPayload {
  organization_name: string
}

export interface AdminOrganizationSyncSettings {
  active_lock: AdminOrganizationSyncLockStatus
  cron_expression: string
  is_enabled: boolean
  last_run: AdminOrganizationSyncRun | null
  sync_company_id: string | null
  sync_company_ids: string[]
  sync_scope: 'all' | 'selected'
  sync_source: string
}

export interface AdminOrganizationSyncSettingsPayload {
  cron_expression: string
  is_enabled: boolean
  sync_company_ids: string[]
  sync_scope: 'all' | 'selected'
}

export interface AdminOrganizationSyncTarget {
  attributes: Record<string, unknown>
  code: string | null
  id: string
  name: string
  status: number
}

export interface AdminAuthzSyncConfig {
  cron_expression: string
  cron_format: 'six-field'
  is_enabled: boolean
  is_initialized: boolean
  last_sync_message: string
  last_sync_result: AdminAuthzSyncResult | null
  last_sync_status: string
  last_sync_time: string | null
  timezone: 'UTC'
}

export interface AdminAuthzSyncEntityResult {
  created: number
  disabled: number
  removed: number
  total: number
  updated: number
}

export interface AdminAuthzSyncResult {
  accounts: AdminAuthzSyncEntityResult
  org_units: AdminAuthzSyncEntityResult
  relations: AdminAuthzSyncEntityResult
  warnings_count: number
}

export interface AdminAuthzSyncConfigPayload {
  cron_expression: string
  is_enabled: boolean
}

const apiClient = createApiClient({
  baseUrl: runtimeConfig.apiBaseUrl,
  getToken: () => getStoredToken('admin'),
  portal: 'admin',
})

function appendOptionalQuery(params: URLSearchParams, key: string, value: string | undefined) {
  const normalized = value?.trim()

  if (normalized) {
    params.set(key, normalized)
  }
}

export async function listOrganizationCompanies() {
  const response = await apiClient.request<
    SuccessEnvelope<{
      data: AdminOrganizationCompany[]
      pagination: {
        page: number
        page_size: number
        total: number
        total_page: number
      }
    }>
  >('/admin/authz/companies')

  return response.data.data
}

export async function getOrganizationTree(companyId: string) {
  const response = await apiClient.request<SuccessEnvelope<AdminOrganizationTreeNode>>(
    `/admin/organization/${encodeURIComponent(companyId)}/tree`,
  )

  return response.data
}

export async function getOrganizationSettings() {
  const response = await apiClient.request<SuccessEnvelope<AdminOrganizationSettings>>(
    '/admin/organization/settings',
  )

  return response.data
}

export async function updateOrganizationSettings(values: AdminOrganizationSettingsPayload) {
  const response = await apiClient.request<SuccessEnvelope<AdminOrganizationSettings>>(
    '/admin/organization/settings',
    {
      body: JSON.stringify(values),
      method: 'PUT',
    },
  )

  return response.data
}

export async function getOrganizationSyncSettings() {
  const response = await apiClient.request<SuccessEnvelope<AdminOrganizationSyncSettings>>(
    '/admin/organization/sync-settings',
  )

  return response.data
}

export async function updateOrganizationSyncSettings(values: AdminOrganizationSyncSettingsPayload) {
  const response = await apiClient.request<SuccessEnvelope<AdminOrganizationSyncSettings>>(
    '/admin/organization/sync-settings',
    {
      body: JSON.stringify(values),
      method: 'PUT',
    },
  )

  return response.data
}

export async function listOrganizationSyncTargets() {
  const response = await apiClient.request<
    SuccessEnvelope<{ items: AdminOrganizationSyncTarget[] }>
  >('/admin/organization/sync-targets')

  return response.data.items
}

export async function listOrganizationSyncRuns(limit = 20) {
  const response = await apiClient.request<SuccessEnvelope<{ items: AdminOrganizationSyncRun[] }>>(
    `/admin/organization/sync-runs?limit=${String(limit)}`,
  )

  return response.data.items
}

export async function getOrganizationSyncStatus() {
  const response = await apiClient.request<SuccessEnvelope<AdminOrganizationSyncLockStatus>>(
    '/admin/organization/sync-status',
  )

  return response.data
}

export async function triggerOrganizationSync(companyIds: string[]) {
  return apiClient.request<MessageEnvelope>('/admin/organization/sync', {
    body: JSON.stringify({ company_ids: companyIds }),
    method: 'POST',
  })
}

export async function forceReleaseOrganizationSync() {
  return apiClient.request<MessageEnvelope>('/admin/organization/sync-status', {
    method: 'DELETE',
  })
}

export async function getAuthzSyncConfig() {
  const response = await apiClient.request<SuccessEnvelope<AdminAuthzSyncConfig>>(
    '/admin/authz/sync-config',
  )

  return response.data
}

export async function updateAuthzSyncConfig(values: AdminAuthzSyncConfigPayload) {
  const response = await apiClient.request<SuccessEnvelope<AdminAuthzSyncConfig>>(
    '/admin/authz/sync-config',
    {
      body: JSON.stringify(values),
      method: 'PUT',
    },
  )

  return response.data
}

export async function runAuthzSync() {
  return apiClient.request<SuccessEnvelope<{ message: string }>>('/admin/authz/sync-config/run', {
    method: 'POST',
  })
}

export async function listMemberAccounts({
  order = 'asc',
  orderBy = 'name',
  orgUnitId,
  page,
  pageSize,
  search,
  status,
}: AdminMemberAccountsParams) {
  const params = new URLSearchParams({
    order,
    order_by: orderBy,
    page: String(page),
    page_size: String(pageSize),
  })

  appendOptionalQuery(params, 'search', search)
  appendOptionalQuery(params, 'org_unit_id', orgUnitId)
  appendOptionalQuery(params, 'status', status)

  const response = await apiClient.request<SuccessEnvelope<AdminMemberAccountsData>>(
    `/admin/organization/member-accounts?${params.toString()}`,
  )

  return response.data
}

export async function getMemberAccountAttributes(userId: string) {
  const response = await apiClient.request<SuccessEnvelope<AdminMemberAccountAttributes>>(
    `/admin/organization/member-accounts/${encodeURIComponent(userId)}/attributes`,
  )

  return response.data
}

export async function createOrganizationDepartment(values: { name: string; parentId: string }) {
  const response = await apiClient.request<SuccessEnvelope<AdminOrganizationTreeNode>>(
    '/admin/organization/departments',
    {
      body: JSON.stringify({
        name: values.name.trim(),
        parent_id: values.parentId,
      }),
      method: 'POST',
    },
  )

  return response.data
}

export async function renameOrganizationDepartment(values: { name: string; orgUnitId: string }) {
  const response = await apiClient.request<SuccessEnvelope<AdminOrganizationTreeNode>>(
    `/admin/organization/departments/${encodeURIComponent(values.orgUnitId)}`,
    {
      body: JSON.stringify({
        name: values.name.trim(),
      }),
      method: 'PUT',
    },
  )

  return response.data
}

export async function deleteOrganizationDepartment(orgUnitId: string) {
  const response = await apiClient.request<MessageEnvelope>(
    `/admin/organization/departments/${encodeURIComponent(orgUnitId)}`,
    {
      method: 'DELETE',
    },
  )

  return response
}

export async function addMemberToDepartment(values: { orgUnitId: string; userId: string }) {
  const response = await apiClient.request<MessageEnvelope>(
    `/admin/organization/departments/${encodeURIComponent(values.orgUnitId)}/members`,
    {
      body: JSON.stringify({
        user_id: values.userId,
      }),
      method: 'POST',
    },
  )

  return response
}

export async function removeMemberFromDepartment(values: { orgUnitId: string; userId: string }) {
  const response = await apiClient.request<MessageEnvelope>(
    `/admin/organization/departments/${encodeURIComponent(values.orgUnitId)}/members/${encodeURIComponent(
      values.userId,
    )}`,
    {
      method: 'DELETE',
    },
  )

  return response
}

export async function batchUpdateUserStatus(userIds: string[], status: 'active' | 'banned') {
  const response = await apiClient.request<BatchSuccessEnvelope<AdminBatchUserStatusResult>>(
    '/admin/user/batch-status',
    {
      body: JSON.stringify({
        status,
        user_ids: userIds,
      }),
      method: 'POST',
    },
  )

  return response
}

export async function updateMemberAccount({ name, userId }: AdminMemberAccountUpdatePayload) {
  const response = await apiClient.request<SuccessEnvelope<Record<string, unknown>>>(
    `/admin/user/${encodeURIComponent(userId)}`,
    {
      body: JSON.stringify({
        name: name.trim(),
      }),
      method: 'PUT',
    },
  )

  return response.data
}

export async function createMemberAccount(values: {
  email: string
  mustChangePassword: boolean
  name: string
}) {
  const response = await apiClient.request<SuccessEnvelope<AdminMemberCreateResult>>(
    '/admin/user',
    {
      body: JSON.stringify({
        email: values.email.trim(),
        name: values.name.trim(),
        must_change_password: values.mustChangePassword,
      }),
      method: 'POST',
    },
  )

  return response.data
}

export async function resetMemberPassword(values: { userId: string }) {
  const response = await apiClient.request<OptionalDataEnvelope<AdminMemberPasswordResetResult>>(
    `/admin/user/${encodeURIComponent(values.userId)}/password`,
    {
      body: JSON.stringify({}),
      method: 'PUT',
    },
  )

  return response.data ?? {}
}
